import {
    cloneElement,
    forwardRef,
    isValidElement,
    useCallback,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
    type Ref,
} from 'react'

import {
    arrow,
    autoUpdate,
    flip,
    limitShift,
    offset,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
    FloatingPortal,
} from '@floating-ui/react'

import { useMergeRefs } from '../../FieldTime/hooks/useMergeRefs'
import type { TooltipProps } from './types'
import {
    ARROW_OFFSET,
    ARROW_PADDING,
    ARROW_SIZE,
    STATIC_SIDE,
    cx,
    hasTooltipContent,
    resolveTooltipPlacement,
} from './utils'

import './Tooltip.scss'

function renderTooltipContent(content: ReactNode): ReactNode {
    if (typeof content !== 'string') return content

    const lines = content.split('\n')
    if (lines.length === 1) return content

    return lines.map((line, index) => (
        <span key={index} className="Tooltip__line">
            {line}
        </span>
    ))
}

export const Tooltip = forwardRef<HTMLElement, TooltipProps>(function Tooltip(
    {
        content,
        children,
        className,
        contentClassName,
        placement = 'top',
        arrow: arrowPosition,
        open: openProp,
        defaultOpen = false,
        onOpenChange,
        disabled = false,
        openDelay = 200,
        closeDelay = 100,
        anchor = null,
        ...rest
    },
    forwardedRef
) {
    const tooltipId = useId()
    const arrowRef = useRef<HTMLSpanElement>(null)
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
    const isControlled = openProp !== undefined
    const open = isControlled ? openProp : uncontrolledOpen
    const enabled = !disabled && hasTooltipContent(content)
    const useAnchor = Boolean(anchor)
    const resolvedPlacement = resolveTooltipPlacement(placement, arrowPosition)

    const setOpen = useCallback(
        (next: boolean) => {
            if (!isControlled) {
                setUncontrolledOpen(next)
            }
            onOpenChange?.(next)
        },
        [isControlled, onOpenChange]
    )

    const { refs, floatingStyles, context, middlewareData, placement: currentPlacement } = useFloating({
        placement: resolvedPlacement,
        strategy: 'fixed',
        open: enabled && open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(ARROW_OFFSET),
            flip({ padding: 8 }),
            shift({ padding: 8, limiter: limitShift() }),
            arrow({ element: arrowRef, padding: ARROW_PADDING }),
        ],
    })

    useLayoutEffect(() => {
        if (anchor) {
            refs.setReference(anchor)
        }
    }, [anchor, refs])

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context, {
            enabled: enabled && !useAnchor,
            move: false,
            delay: { open: openDelay, close: closeDelay },
        }),
        useFocus(context, { enabled: enabled && !useAnchor }),
        useDismiss(context, { ancestorScroll: true }),
        useRole(context, { role: 'tooltip' }),
    ])

    const child = isValidElement(children)
        ? (children as ReactElement<{ className?: string }> & { ref?: Ref<HTMLElement> })
        : null
    const referenceRef = useMergeRefs([refs.setReference, forwardedRef, child?.ref])
    const side = currentPlacement.split('-')[0]
    const arrowX = middlewareData.arrow?.x
    const arrowY = middlewareData.arrow?.y
    const staticSide = STATIC_SIDE[side] ?? 'bottom'

    const arrowStyle = useMemo(
        () => ({
            width: ARROW_SIZE,
            height: ARROW_SIZE,
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            [staticSide]: `${-ARROW_SIZE / 2}px`,
        }),
        [arrowX, arrowY, staticSide]
    )

    const isOpen = enabled && open
    const describedBy = isOpen ? tooltipId : undefined

    const trigger = useAnchor
        ? children
        : child
          ? cloneElement(
                child,
                getReferenceProps({
                    ref: referenceRef,
                    className: cx(child.props.className, className),
                    'aria-describedby': describedBy,
                    ...rest,
                })
            )
          : (
                <span
                    ref={referenceRef}
                    className={cx('Tooltip', className)}
                    {...getReferenceProps(rest)}
                    aria-describedby={describedBy}
                >
                    {children}
                </span>
            )

    return (
        <>
            {trigger}
            {isOpen ? (
                <FloatingPortal>
                    <div
                        id={tooltipId}
                        ref={refs.setFloating}
                        className={cx('Tooltip__content', contentClassName)}
                        style={floatingStyles}
                        data-placement={currentPlacement}
                        data-arrow={arrowPosition}
                        {...getFloatingProps()}
                    >
                        {renderTooltipContent(content)}
                        <span ref={arrowRef} className="Tooltip__arrow" style={arrowStyle} aria-hidden />
                    </div>
                </FloatingPortal>
            ) : null}
        </>
    )
})

Tooltip.displayName = 'Tooltip'

export default Tooltip
